import { useCallback, useEffect, useState } from "react";
import { Copy, Check, RefreshCw, Loader2, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_STYLE = {
  queued: "bg-slate-100 text-slate-700", accepted: "bg-slate-100 text-slate-700", sending: "bg-slate-100 text-slate-700",
  sent: "bg-sky-100 text-sky-800", delivered: "bg-emerald-100 text-emerald-800", read: "bg-emerald-200 text-emerald-900",
  failed: "bg-rose-100 text-rose-800", undelivered: "bg-rose-100 text-rose-800", received: "bg-violet-100 text-violet-800",
};

function CopyField({ label, value, testid }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 break-all" data-testid={testid}>{value}</code>
        <button type="button" onClick={copy} className="pm-btn-secondary h-9 w-9 rounded-lg flex items-center justify-center" data-testid={`${testid}-copy`}>{done ? <Check size={14} /> : <Copy size={14} />}</button>
      </div>
    </div>
  );
}

function StatusPill({ status, errorCode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[status] || "bg-slate-100 text-slate-700"}`} data-testid="wa-msg-status">
      {status}{errorCode ? ` · ${errorCode}` : ""}
    </span>
  );
}

function MessageRow({ m }) {
  const out = m.direction === "outbound";
  return (
    <div className="pm-card p-4 flex gap-3" data-testid={`wa-msg-${m.direction}`}>
      <div className={`mt-0.5 h-7 w-7 rounded-full flex items-center justify-center shrink-0 ${out ? "bg-slate-900 text-white" : "bg-violet-100 text-violet-800"}`}>
        {out ? <ArrowUpRight size={14} /> : <ArrowDownLeft size={14} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-900">{out ? `→ ${m.to}` : `← ${m.from}`}</span>
          <span>{(m.created_at || "").slice(0, 19).replace("T", " ")}</span>
          {out && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] uppercase tracking-wide">{m.mode}</span>}
          <StatusPill status={m.status} errorCode={m.error_code} />
        </div>
        <div className="text-sm mt-1 whitespace-pre-wrap break-words">{m.body || (m.content_sid ? `Template ${m.content_sid}${m.variables ? " · " + JSON.stringify(m.variables) : ""}` : <span className="text-slate-400">(media)</span>)}</div>
        {m.error_message && <div className="text-xs text-rose-600 mt-1" data-testid="wa-msg-error">{m.error_message}</div>}
        {m.sid && <div className="text-[10px] text-slate-400 mt-1 font-mono">{m.sid}</div>}
      </div>
    </div>
  );
}

export default function AdminWhatsAppTest() {
  const [cfg, setCfg] = useState(null);
  const [err, setErr] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("template");
  const [contentSid, setContentSid] = useState("");
  const [vars, setVars] = useState("");
  const [body, setBody] = useState("");
  const [win, setWin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sendErr, setSendErr] = useState("");

  const loadMsgs = useCallback(() => api.get("/admin/whatsapp/messages").then((r) => setMsgs(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/admin/whatsapp/config").then((r) => setCfg(r.data)).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail)));
    loadMsgs();
    const t = setInterval(loadMsgs, 5000);
    return () => clearInterval(t);
  }, [loadMsgs]);

  const checkWindow = async () => {
    if (to.replace(/\D/g, "").length < 8) return setWin(null);
    try { const { data } = await api.get("/admin/whatsapp/window", { params: { to } }); setWin(data); } catch { setWin(null); }
  };

  const send = async () => {
    setBusy(true); setSendErr("");
    let variables = null;
    if (mode === "template" && vars.trim()) {
      try { variables = JSON.parse(vars); } catch { setSendErr('Variabelen moeten geldige JSON zijn, bv. {"1":"Enrico"}'); setBusy(false); return; }
    }
    try {
      await api.post("/admin/whatsapp/send", { to, mode, content_sid: mode === "template" ? contentSid : null, variables, body: mode === "freeform" ? body : null });
      if (mode === "freeform") setBody("");
      await loadMsgs();
    } catch (e) { setSendErr(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (err) return <Layout title="WhatsApp test"><div className="text-sm text-rose-600" data-testid="wa-admin-error">{err}</div></Layout>;
  const tpl = cfg?.templates?.find((t) => t.sid === contentSid);
  const canSend = to.replace(/\D/g, "").length >= 8 && (mode === "template" ? contentSid.startsWith("HX") : body.trim());

  return (
    <Layout title="Admin · WhatsApp test">
      <div className="grid lg:grid-cols-[420px_1fr] gap-6" data-testid="wa-admin-page">
        <div className="space-y-6">
          <div className="pm-card p-5 space-y-4" data-testid="wa-send-card">
            <div>
              <h3 className="font-bold">Testbericht sturen</h3>
              <div className="text-xs text-slate-500 mt-0.5">Afzender: <span className="font-mono" data-testid="wa-from">{cfg?.from || "…"}</span></div>
            </div>
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Ontvanger (E.164)</div>
              <input className="pm-input w-full" placeholder="+31612345678" value={to} onChange={(e) => setTo(e.target.value)} onBlur={checkWindow} data-testid="wa-to" />
            </label>
            {win && (
              <div className={`text-xs rounded-lg px-3 py-2 ${win.open ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="wa-window">
                {win.open ? `24-uursvenster open tot ${win.until.slice(0, 16).replace("T", " ")} UTC — vrije tekst toegestaan.` : "Geen inkomend bericht in de laatste 24 uur — alleen goedgekeurde templates worden afgeleverd."}
              </div>
            )}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100" data-testid="wa-mode">
              {[["template", "Template"], ["freeform", "Vrije tekst (24u)"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMode(k)} className={`h-9 rounded-lg text-xs font-semibold transition-colors ${mode === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid={`wa-mode-${k}`}>{l}</button>
              ))}
            </div>
            {mode === "template" ? (
              <>
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Template (Content SID)</div>
                  <select className="pm-input w-full" value={cfg?.templates?.some((t) => t.sid === contentSid) ? contentSid : ""} onChange={(e) => setContentSid(e.target.value)} data-testid="wa-template-select">
                    <option value="">— kies een template —</option>
                    {(cfg?.templates || []).map((t) => <option key={t.sid} value={t.sid}>{t.name} · {t.status}{t.language ? ` · ${t.language}` : ""}</option>)}
                  </select>
                  <input className="pm-input w-full mt-2 font-mono text-xs" placeholder="of plak een Content SID: HX…" value={contentSid} onChange={(e) => setContentSid(e.target.value.trim())} data-testid="wa-content-sid" />
                </label>
                {tpl?.body && <div className="text-xs text-slate-600 bg-slate-50 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid="wa-template-preview">{tpl.body}</div>}
                <label className="block">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Variabelen (JSON){tpl?.variables?.length ? ` — ${tpl.variables.join(", ")}` : ""}</div>
                  <input className="pm-input w-full font-mono text-xs" placeholder='{"1":"Enrico","2":"Ajax"}' value={vars} onChange={(e) => setVars(e.target.value)} data-testid="wa-variables" />
                </label>
              </>
            ) : (
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Bericht</div>
                <textarea className="pm-input w-full min-h-[96px]" placeholder="Hallo, dit is een testbericht van Playmaker." value={body} onChange={(e) => setBody(e.target.value)} data-testid="wa-body" />
              </label>
            )}
            {sendErr && <div className="text-xs text-rose-600" data-testid="wa-send-error">{sendErr}</div>}
            <button type="button" onClick={send} disabled={busy || !canSend} className="pm-btn-primary w-full h-11 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50" data-testid="wa-send">
              {busy ? <Loader2 size={14} className="animate-spin" /> : "Versturen via WhatsApp"}
            </button>
          </div>

          <div className="pm-card p-5 space-y-4" data-testid="wa-webhooks-card">
            <div>
              <h3 className="font-bold">Twilio-configuratie</h3>
              <div className="text-xs text-slate-500 mt-0.5">Plak deze URL's bij de WhatsApp-sender in de Twilio Console. Account {cfg?.account_sid_hint}.</div>
            </div>
            {cfg && <CopyField label="When a message comes in (inbound webhook, POST)" value={cfg.inbound_webhook_url} testid="wa-inbound-url" />}
            {cfg && <CopyField label="Status callback URL (POST)" value={cfg.status_callback_url} testid="wa-status-url" />}
          </div>
        </div>

        <div className="space-y-3" data-testid="wa-log">
          <div className="flex items-center justify-between">
            <h3 className="font-bold">Berichtenlog <span className="text-xs text-slate-400 font-normal">({msgs.length}) · ververst elke 5 s</span></h3>
            <button type="button" onClick={loadMsgs} className="pm-btn-secondary h-8 px-3 rounded-lg text-xs flex items-center gap-1" data-testid="wa-refresh"><RefreshCw size={12} /> Verversen</button>
          </div>
          {msgs.length === 0 && <div className="text-sm text-slate-500 pm-card p-5" data-testid="wa-log-empty">Nog geen berichten. Stuur een testbericht of laat de ontvanger een WhatsApp naar {cfg?.from?.replace("whatsapp:", "")} sturen.</div>}
          {msgs.map((m) => <MessageRow key={m.id} m={m} />)}
        </div>
      </div>
    </Layout>
  );
}
